import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, CssBaseline, TextField, Link, Paper, Typography,
    InputAdornment, IconButton, CircularProgress
} from '@mui/material';

// Ícones
import PsychologyIcon from '@mui/icons-material/Psychology';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { loginUsuario } from '../../services/api';
import { setSessionCookie } from '../../services/auth';
import { toastSuccess, toastError, alertValidacao, confirmAcao } from '../../services/alerts';

const Login = () => {
    const navigate = useNavigate();

    const [credentials, setCredentials] = useState({ tenant: '', username: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleChange = (prop) => (event) => {
        setCredentials({ ...credentials, [prop]: event.target.value });
    };

    const handleClickShowPassword = () => setShowPassword(!showPassword);

    const handleLogin = async (event, force = false) => {
        if (event) event.preventDefault();

        if (!credentials.tenant || !credentials.username || !credentials.password) {
            alertValidacao('Por favor, preencha todos os campos.');
            return;
        }

        setLoading(true);

        try {
            const data = await loginUsuario({
                tenant: credentials.tenant.trim().toLowerCase(),
                usuario: credentials.username,
                senha: credentials.password
            }, force);

            const { token, user } = data;
            const tokenToSave = token || data.access_token || (typeof data === 'string' ? data : null);

            if (tokenToSave) setSessionCookie(tokenToSave);

            if (user) {
                localStorage.setItem('user_name', user.nome || user.username || 'Usuário');
                localStorage.setItem('user_login', user.username || credentials.username);
            } else {
                localStorage.setItem('user_name', credentials.username);
                localStorage.setItem('user_login', credentials.username);
            }

            toastSuccess('Login efetuado com sucesso');
            navigate('/dashboard');

        } catch (err) {
            console.error("Erro no login:", err);
            if (err.response && err.response.status === 409) {
                setLoading(false);
                const confirmar = await confirmAcao({
                    title: 'Sessão já ativa',
                    text: 'Este utilizador já tem uma sessão ativa noutro dispositivo ou navegador. Deseja desconectar a sessão anterior e entrar aqui?',
                    icon: 'warning',
                    confirmButtonText: 'Desconectar e entrar',
                    cancelButtonText: 'Cancelar',
                });
                if (confirmar) {
                    handleLogin(null, true);
                }
                return;
            } else if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                toastError('Usuário ou senha incorretos.');
            } else if (err.code === "ERR_NETWORK") {
                toastError('Erro de conexão. Verifique se o servidor está a correr.');
            } else {
                toastError('Ocorreu um erro ao tentar entrar. Tente novamente.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box component="main" sx={{ display: 'flex', minHeight: '100vh', overflow: 'hidden' }}>
            <CssBaseline />

            {/* --- LADO ESQUERDO: Fundo Azul (Cresce dinamicamente) --- */}
            <Box
                sx={{
                    flexGrow: 1, // Faz com que ocupe todo o espaço livre
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 30%, #1A2836 90%)`,
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: 'primary.contrastText',
                    position: 'relative',
                    zIndex: 1
                }}
            >
                <Box sx={{ position: 'absolute', width: '150%', height: '150%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0) 60%)', top: '-50%', left: '-50%', zIndex: 0, pointerEvents: 'none' }} />
                <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center', p: 4 }}>
                    <Box sx={{ display: 'inline-flex', p: 3, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)', mb: 3, backdropFilter: 'blur(10px)', boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)' }}>
                        <PsychologyIcon sx={{ fontSize: 100, color: 'primary.main' }} />
                    </Box>
                    <Typography component="h1" variant="h1" fontWeight="800" sx={{ color: '#fff', letterSpacing: 4, fontSize: { xs: '3rem', md: '5rem', lg: '6rem'} }}>
                        SIGAPSI
                    </Typography>
                    <Typography variant="h5" sx={{ mt: 2, fontWeight: 300, color: 'primary.contrastText', opacity: 0.9, letterSpacing: 1 }}>
                        Sistema Integrado de Gestão de Atendimentos Psicológicos
                    </Typography>
                </Box>
            </Box>

            {/* --- LADO DIREITO: Formulário (Largura Fixa Colado à Direita) --- */}
            <Paper
                elevation={0}
                square
                sx={{
                    width: { xs: '100%', md: '450px', lg: '500px' }, // Largura fixa no desktop!
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    zIndex: 2,
                    boxShadow: '-10px 0 30px rgba(0,0,0,0.1)', // Sombra para separar do fundo azul
                    bgcolor: '#fff'
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '400px', // Limita apenas os inputs dentro do painel
                        p: { xs: 4, sm: 5 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, mb: 2 }}>
                        <PsychologyIcon sx={{ fontSize: 60, color: 'primary.main' }} />
                    </Box>

                    <Box sx={{ m: 1, bgcolor: 'rgba(26, 187, 156, 0.1)', p: 1.5, borderRadius: '16px', color: 'primary.main', mb: 2 }}>
                        <LockOutlinedIcon fontSize="large" />
                    </Box>

                    <Typography component="h1" variant="h5" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>
                        Bem-vindo ao SigaPsi
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                        Insira as suas credenciais para aceder.
                    </Typography>

                    <Box component="form" noValidate onSubmit={(e) => handleLogin(e, false)} sx={{ width: '100%' }}>

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="tenant"
                            label="Consultório"
                            name="tenant"
                            autoFocus
                            placeholder="código do consultório"
                            value={credentials.tenant}
                            onChange={handleChange('tenant')}
                            disabled={loading}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="username"
                            label="Utilizador"
                            name="username"
                            value={credentials.username}
                            onChange={handleChange('username')}
                            disabled={loading}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Palavra-passe"
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            value={credentials.password}
                            onChange={handleChange('password')}
                            disabled={loading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleClickShowPassword} edge="end">
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 3 }}>

                        </Box>

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={loading}
                            sx={{ mt: 1, py: 1.5, fontSize: '1.1rem' }}
                            startIcon={!loading && <LoginIcon />}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Entrar no Sistema'}
                        </Button>
                    </Box>
                </Box>
            </Paper>

        </Box>
    );
};

export default Login;